import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { animate, style, transition, trigger, state } from '@angular/animations';

interface MenuItem {
  label: string;
  link: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslateModule, TranslatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  host: { id: 'app-header-main' },
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('0.5s ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('mobileMenu', [
      state('void', style({
        opacity: 0
      })),
      state('*', style({
        opacity: 1
      })),
      transition('void => *', [
        style({
          opacity: 0,
          transform: '{{transformDirection}}'
        }),
        animate('0.3s ease-in-out', style({
          opacity: 1,
          transform: 'translateX(0)'
        }))
      ], { params: { transformDirection: 'translateX(100%)' } }),
      transition('* => void', [
        style({
          opacity: 1,
          transform: 'translateX(0)'
        }),
        animate('0.3s ease-in-out', style({
          opacity: 0,
          transform: '{{transformDirection}}'
        }))
      ], { params: { transformDirection: 'translateX(100%)' } })
    ]),
    trigger('menuIcon', [
      transition('* => *', [
        animate('0.2s ease-in-out')
      ])
    ])
  ]
})
export class HeaderComponent implements OnInit {
  isMobileMenuOpen = false;
  currentLang: string;
  menuItems: MenuItem[] = [];

  // Fallback menu items in case translation fails
  private defaultMenuItems: MenuItem[] = [
    { label: 'Home', link: '/' },
    { label: 'Features', link: '/features' },
    { label: 'Pricing', link: '/pricing' },
    { label: 'Testimonials', link: '/testimonials' },
    { label: 'Contact Us', link: '/contact-us' }
  ];

  constructor(private translateService: TranslateService) {
    this.currentLang = this.translateService.currentLang || 'en';

    // Initialize with default menu items
    this.menuItems = [...this.defaultMenuItems];
  }

  ngOnInit(): void {
    // Initial language setup
    if (!this.translateService.currentLang) {
      this.translateService.use(this.currentLang);
    }

    // Subscribe to language changes
    this.translateService.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
      this.updateMenuItems();
    });

    // Initialize menu items
    this.updateMenuItems();
  }

  updateMenuItems(): void {
    this.translateService.get('header.menu').subscribe({
      next: (menuData: any) => {
        if (Array.isArray(menuData) && menuData.length > 0) {
          this.menuItems = menuData;
        } else {
          console.warn('header.menu is not a valid array, using fallback:', menuData);
          this.menuItems = this.defaultMenuItems;
        }
      },
      error: (err) => {
        console.error('Error loading menu items:', err);
        this.menuItems = this.defaultMenuItems;
      }
    });
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  switchLanguage() {
    const newLang = this.currentLang === 'en' ? 'ar' : 'en';
    this.translateService.use(newLang);
  }

  get languageLabel(): string {
    return this.currentLang === 'en' ? 'العربية' : 'English';
  }

  get mobileMenuAnimationParams() {
    return {
      transformDirection: this.currentLang === 'ar' ? 'translateX(-100%)' : 'translateX(100%)'
    };
  }
}
